// Global
import { LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import { defaultConfig, tv } from 'tailwind-variants';

// Local
import { SiteStructure } from '.generated/Feature.CW.model';
import { withStandardComponentWrapper } from 'helpers/HOC';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { ComponentProps } from 'lib/component-props';
import { useCurrentScreenType } from 'lib/utils/get-screen-type';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import classNames from 'classnames';
import { useRouter } from 'next/router';

// Tailwind Styles
defaultConfig.twMerge = false;

// Types
export type SocialMediaProps = ComponentProps &
  SiteStructure.Footer.SocialMedia_Component & {
    fields: {
      socialMediaLinks: Array<SiteStructure.Footer.SocialMediaItem_Item>;
    };
  };

const SocialMedia = (props: SocialMediaProps): JSX.Element => {
  const { socialMediaLinks } = props?.fields || {};
  const { RenderingIdentifier } = props?.params || {};
  const { currentScreenWidth } = useCurrentScreenType();
  const router = useRouter();
  // Detect private mode
  const isPrivateMode = router?.query?.private === 'true';

  /*
   * Rendering
   */

  if (!props?.fields) {
    return <></>;
  }

  const extendedTailwindVariants = tv({
    extend: TAILWIND_VARIANTS,
    slots: {
      base: [props?.params?.styles],
    },
  });

  const { base, iconList, link, wrapper, svg } = extendedTailwindVariants();

  return !isPrivateMode ? (
    <div
      className={base()}
      data-component="authorable/shared/site-structure/footer/socialmedia"
      id={RenderingIdentifier}
    >
      <div className={wrapper()}>
        <ul className={iconList()}>
          {socialMediaLinks?.map((icon: SiteStructure.Footer.SocialMediaItem_Item) => {
            return (
              <li key={icon?.id}>
                <LinkWrapper
                  className={link()}
                  field={icon?.fields?.socialMediaLink as LinkField}
                  suppressNewTabIcon={true}
                  srOnlyText={icon?.name}
                >
                  <ImageWrapper
                    field={icon?.fields?.socialMediaLogo}
                    className={classNames(
                      svg(),
                      currentScreenWidth > 1024 ? '!w-12 !h-12' : '!w-10 !h-10'
                    )}
                  />
                </LinkWrapper>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  ) : (
    <></>
  );
};

export const Default = withStandardComponentWrapper(SocialMedia);

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['theme-lightblue', 'bg-theme-bg'],
    iconList: ['flex', 'flex-row', 'gap-4', 'w-fit', 'flex-wrap'],
    link: [
      'group',
      'text-theme-black-800',
      'hover:text-theme-black-500',
      '!w-21 !h-21 lg:!w-24 lg:!h-24',
      'inline-flex',
      'justify-center',
      'items-center',
      'bg-blue-100',
      'rounded-full',
      'hover:bg-blue-700',
      'transition-all duration-300',
      'print:hidden',
    ],
    svg: [
      'text-blue-700',
      'group-hover:text-white',
      'object-contain',
      'object-center',
      'group-hover:brightness-0',
      'group-hover:invert-[1]',
    ],
    wrapper: ['max-w-screen-xxl', 'm-auto', 'px-10', 'lg:px-20', 'pb-16', 'pt-8'],
  },
});
